import React, { useEffect, useState } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  Grid,
  CircularProgress,
  Alert,
  tableCellClasses,
} from '@mui/material';
import styled_ from '@emotion/styled';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import {
  fetchPartyWiseOutstanding,
  downloadPartyWiseOutstanding
} from '../../store/reportStore/reportThunk';
import { PartyWiseOutstandingItem, PartyWiseOutstandingFetch } from '../../utils/report.types';
import { ApiStatus } from '../../utils/api.types';
import { toast } from 'react-toastify';
import { Utils, VocuherCategory } from '../../utils/common-utils';
import ReusableReportHeader from './ReusableReportHeader';
import Filters from '../Filters/Filters';
import moment from 'moment';

// Styled components - GSTR-2 Purchase Summary Style
const StyledTableCell = styled_(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: '#eaecf0',
    color: '#000',
    border: '0.75px solid #d9d4d4',
    margin: 0,
    padding: 5,
    fontSize: '9pt',
    paddingLeft: '15px !important',
  },
  [`&.${tableCellClasses.footer}`]: {
    backgroundColor: '#f5f5f5',
    color: 'black',
    borderTop: '0.25px solid #d9d4d4',
    borderBottom: '0.25px solid #d9d4d4',
    left: 0,
    bottom: 0,
    zIndex: 2,
    position: 'sticky'
  },
  [`&.${tableCellClasses.body}`]: {
    verticalAlign: 'super',
    fontSize: '9pt',
    fontWeight: 'normal',
    color: '#292d35',
    padding: 5,
    border: '0.75px solid #d9d4d4',
    paddingLeft: '15px !important',
    margin: 0
  },
}));

const StyledTableRow = styled_(TableRow)(({ theme }) => ({
  // Using the same empty style as GSTR-2 report
}));

interface PartyWiseOutstandingProps {
  type: string;
  rawData?: any;
}

const PartyWiseOutstanding: React.FC<PartyWiseOutstandingProps> = ({ type, rawData }) => {
  const dispatch = useAppDispatch();
  const { status } = useAppSelector(state => state.reports);
  const { selectedAccount } = useAppSelector(state => state.auth);

  // State management
  const [reportData, setReportData] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [filters, setFilters] = useState('');
  const [filterData, setFilterData] = useState<any>();
  const [sortBy, setSortBy] = useState('partyName');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');

  // Filter handlers
  const filterOnChangeHandler = (data: any) => {
    setFilters(data);
  };

  const filterdOnChangeHandler = (data: any) => {
    setFilterData(data);
  };

  // Load data on filter changes - exactly like PartyLedger
  useEffect(() => {
    const { _id: id } = selectedAccount;
    if (filters) {
      setIsLoading(true);

      // Parse filters query string to get dates and party
      const urlParams = new URLSearchParams(filters);
      const start = urlParams.get('start');
      const end = urlParams.get('end');
      const partyId = urlParams.get('filter');

      const params: PartyWiseOutstandingFetch = {
        accountId: id,
        partyId: partyId || undefined,
        startDate: start || moment().format('YYYY-MM-DD'),
        endDate: end || moment().format('YYYY-MM-DD'),
        excludeVoucherTypes: 'Quotation',
        sortBy,
        sortOrder
      };

      console.log('Fetching with params:', params);

      dispatch(fetchPartyWiseOutstanding(params)).unwrap().then((data) => {
        if (data) {
          setReportData(data);
          setIsLoading(false);
        }
      }).catch((error: any) => {
        console.error('Fetch error:', error);
        toast.error(error.message || 'Failed to fetch party wise outstanding report');
        setIsLoading(false);
      });
    } else {
      // When filters is empty, stop loading
      setIsLoading(false);
    }
  }, [dispatch, selectedAccount, filters]);

  // Handle download functions
  const handleDownloadPDF = async () => {
    if (!selectedAccount?._id || !filters) return false;

    // Parse filters query string to get dates and party
    const urlParams = new URLSearchParams(filters);
    const start = urlParams.get('start');
    const end = urlParams.get('end');
    const partyId = urlParams.get('filter');

    const params: PartyWiseOutstandingFetch = {
      accountId: selectedAccount._id,
      partyId: partyId || undefined,
      startDate: start || moment().format('YYYY-MM-DD'),
      endDate: end || moment().format('YYYY-MM-DD'),
      excludeVoucherTypes: 'Quotation',
      sortBy,
      sortOrder,
      isDownloadPdf: 'true'
    };

    const response = await dispatch(downloadPartyWiseOutstanding(params)).unwrap();
    if (response) {
      const blob = new Blob([response], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `party_wise_outstanding_${new Date().toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    }
  };

  const handleDownloadExcel = async () => {
    if (!selectedAccount?._id || !filters) return false;

    // Parse filters query string to get dates and party
    const urlParams = new URLSearchParams(filters);
    const start = urlParams.get('start');
    const end = urlParams.get('end');
    const partyId = urlParams.get('filter');

    const params: PartyWiseOutstandingFetch = {
      accountId: selectedAccount._id,
      partyId: partyId || undefined,
      startDate: start || moment().format('YYYY-MM-DD'),
      endDate: end || moment().format('YYYY-MM-DD'),
      excludeVoucherTypes: 'Quotation',
      sortBy,
      sortOrder,
      isDownloadExcel: 'true'
    };

    const response = await dispatch(downloadPartyWiseOutstanding(params)).unwrap();
    if (response) {
      const blob = new Blob([response], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `party_wise_outstanding_${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    }
  };


  return (
    <Box sx={{ width: 'calc(100% - 7px)', overflow: 'hidden', mb: 2 }} className={'c-card2'}>
      {/* Report Header */}
      <ReusableReportHeader
        reportType="PARTY_WISE_OUTSTANDING"
        title="Party Wise Outstanding"
        subtitle={(filterData && filterData.periodString) ? filterData.periodString : 'Outstanding amounts by party with aging analysis'}
        recordCount={reportData?.data?.summary?.totalRecords || 0}
        isLoading={isLoading}
        onDownloadPDF={handleDownloadPDF}
        onDownloadExcel={handleDownloadExcel}
        showPrint={false}
        showShare={false}
      />

      {/* Filters Section - Outside conditional render to maintain state */}
      <Grid container px={2} py={1.5} style={{ borderBottom: '.5px solid #9da7b7' }}>
        <Grid xs={12}>
          <Filters type={VocuherCategory.SALE} filterHandler={filterOnChangeHandler} selectedFilters={filterdOnChangeHandler} hideFilter={true} />
        </Grid>
      </Grid>

      {!isLoading && status !== ApiStatus.LOADING ? (
        <>
          <div style={{ marginBottom: 12 }} id='pdfContainer'>

          {/* Data Table */}
              <Table stickyHeader sx={{ minWidth: 700, marginBottom: '100px' }} size='small' aria-label="customized table">
            <TableHead>
              <TableRow>
                <StyledTableCell width={250}>Party Name</StyledTableCell>
                <StyledTableCell>Invoice No</StyledTableCell>
                <StyledTableCell>Date</StyledTableCell>
                <StyledTableCell>Due Date</StyledTableCell>
                <StyledTableCell align="right">Invoice Amt</StyledTableCell>
                <StyledTableCell align="right">Received</StyledTableCell>
                <StyledTableCell align="right">Outstanding</StyledTableCell>
                <StyledTableCell align="right">0–30 Days</StyledTableCell>
                <StyledTableCell align="right">31–60 Days</StyledTableCell>
                <StyledTableCell align="right">61–90 Days</StyledTableCell>
                <StyledTableCell align="right">90+ Days</StyledTableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <StyledTableCell colSpan={11} align="center" sx={{ py: 4 }}>
                    <CircularProgress size={24} />
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      Loading...
                    </Typography>
                  </StyledTableCell>
                </TableRow>
              ) : (reportData?.data?.items?.length) ? (
                <>
                  {reportData.data.items.map((item: PartyWiseOutstandingItem) => (
                    <StyledTableRow key={item._id}>
                      <StyledTableCell>{item.partyName}</StyledTableCell>
                      <StyledTableCell>{item.invoiceNo}</StyledTableCell>
                      <StyledTableCell>{moment(item.date).format('DD/MM/YYYY')}</StyledTableCell>
                      <StyledTableCell>{moment(item.dueDate).format('DD/MM/YYYY')}</StyledTableCell>
                      <StyledTableCell align="right">{Utils.formatNumber(item.invoiceAmt || 0, false, true)}</StyledTableCell>
                      <StyledTableCell align="right">{Utils.formatNumber(item.received || 0, false, true)}</StyledTableCell>
                      <StyledTableCell align="right" sx={{ fontWeight: 'bold' }}>{Utils.formatNumber(item.outstanding || 0, false, true)}</StyledTableCell>
                      <StyledTableCell align="right">{Utils.formatNumber(item.aging_0_30 || 0, false, true)}</StyledTableCell>
                      <StyledTableCell align="right">{Utils.formatNumber(item.aging_31_60 || 0, false, true)}</StyledTableCell>
                      <StyledTableCell align="right">{Utils.formatNumber(item.aging_61_90 || 0, false, true)}</StyledTableCell>
                      <StyledTableCell align="right">{Utils.formatNumber(item.aging_90_plus || 0, false, true)}</StyledTableCell>
                    </StyledTableRow>
                  ))}
                  {/* Summary Row */}
                  <StyledTableRow>
                    <StyledTableCell colSpan={4} sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>
                      TOTAL
                    </StyledTableCell>
                    <StyledTableCell align="right" sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>
                      {Utils.formatNumber(reportData.data.summary.totalInvoiceAmt || 0, false, true)}
                    </StyledTableCell>
                    <StyledTableCell align="right" sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>
                      {Utils.formatNumber(reportData.data.summary.totalReceived || 0, false, true)}
                    </StyledTableCell>
                    <StyledTableCell align="right" sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>
                      {Utils.formatNumber(reportData.data.summary.totalOutstanding || 0, false, true)}
                    </StyledTableCell>
                    <StyledTableCell align="right" sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>
                      {Utils.formatNumber(reportData.data.summary.total_0_30 || 0, false, true)}
                    </StyledTableCell>
                    <StyledTableCell align="right" sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>
                      {Utils.formatNumber(reportData.data.summary.total_31_60 || 0, false, true)}
                    </StyledTableCell>
                    <StyledTableCell align="right" sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>
                      {Utils.formatNumber(reportData.data.summary.total_61_90 || 0, false, true)}
                    </StyledTableCell>
                    <StyledTableCell align="right" sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>
                      {Utils.formatNumber(reportData.data.summary.total_90_plus || 0, false, true)}
                    </StyledTableCell>
                  </StyledTableRow>
                </>
              ) : (
                <TableRow>
                  <StyledTableCell colSpan={11} align="center" sx={{ py: 4 }}>
                    <Typography variant="body1" color="text.secondary">
                      No outstanding records found
                    </Typography>
                  </StyledTableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>

          </div>
        </>
      ) : (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          {status === ApiStatus.FAILED ? (
            <Alert severity="error" sx={{ m: 2 }}>
              Failed to load party wise outstanding report. Please try again.
            </Alert>
          ) : (
            <CircularProgress color="info" />
          )}
        </Box>
      )}
    </Box>
  );
};

export default PartyWiseOutstanding;
